import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg,
    Eye,
    InfoCircle,
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { DialogTrigger as AriaDialogTrigger, Popover as AriaPopover, Button as AriaButton } from 'react-aria-components';
import { workflowService } from '@/data/mock-workflows';
import type { Workflow, WorkflowStatus } from '@/types/workflow';
import { useTranslation } from 'react-i18next';

export const WorkflowsListPage = () => {
    const { t } = useTranslation(['pages:workflows-admin', 'common']);
    const navigate = useNavigate();
    const [workflows, setWorkflows] = useState<Workflow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<WorkflowStatus | ''>('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null);

    const statusOptions = [
        { id: '', label: t('workflows.statusPlaceholder') },
        { id: 'active', label: t('workflows.status.active') },
        { id: 'inactive', label: t('workflows.status.inactive') },
        { id: 'draft', label: t('workflows.status.draft') },
    ];

    useEffect(() => {
        loadWorkflows();
    }, [searchTerm, statusFilter]);

    const loadWorkflows = () => {
        setIsLoading(true);
        let filtered = workflowService.getAll();

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(
                (w) =>
                    w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    w.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply status filter
        if (statusFilter) {
            filtered = filtered.filter((w) => w.status === statusFilter);
        }

        setWorkflows(filtered);
        setIsLoading(false);
    };

    const handleDeleteClick = (id: string) => {
        setWorkflowToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (workflowToDelete) {
            try {
                workflowService.delete(workflowToDelete);
                loadWorkflows();
            } catch (error) {
                // Error already handled by service (e.g., cannot delete default)
                console.error('Error deleting workflow:', error);
            }
            setIsDeleteModalOpen(false);
            setWorkflowToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setWorkflowToDelete(null);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('common:loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('workflows.title')}</h2>
                    <p className="text-tertiary mt-1">{t('workflows.description')}</p>
                </div>
                <Button
                    onClick={() => navigate('/admin/workflows/new')}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('workflows.newWorkflow')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        placeholder={t('workflows.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('workflows.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as WorkflowStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Workflows Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('workflows.title')} (${workflows.length})`}
                    description={t('workflows.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('workflows.headers.name')} isRowHeader />
                        <Table.Head label={t('workflows.headers.type')} />
                        <Table.Head label={t('workflows.headers.status')} />
                        <Table.Head label={t('workflows.headers.nodesCount')} />
                        <Table.Head label={t('workflows.headers.createdAt')} />
                        <Table.Head label={t('workflows.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {workflows.map((workflow) => (
                            <Table.Row key={workflow.workflow_id}>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <div className="font-medium text-primary">{workflow.name}</div>
                                        {workflow.description && (
                                            <AriaDialogTrigger>
                                                <AriaButton className="flex cursor-pointer items-center justify-center rounded-md p-0.5 text-tertiary outline-focus-ring transition duration-100 ease-linear hover:text-secondary focus-visible:outline-2 focus-visible:outline-offset-2">
                                                    <InfoCircle className="size-4" />
                                                </AriaButton>
                                                <AriaPopover
                                                    placement="bottom start"
                                                    offset={8}
                                                    className="max-w-xs rounded-lg bg-primary p-3 shadow-lg ring-1 ring-secondary outline-hidden"
                                                >
                                                    <div className="text-sm text-primary">{workflow.description}</div>
                                                </AriaPopover>
                                            </AriaDialogTrigger>
                                        )}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    {workflow.is_default ? (
                                        <Badge color="brand" size="sm">{t('workflows.type.default')}</Badge>
                                    ) : (
                                        <Badge color="gray" size="sm">{t('workflows.type.custom')}</Badge>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {workflow.status === 'active' ? (
                                        <Badge color="success" size="sm">{t('workflows.status.active')}</Badge>
                                    ) : workflow.status === 'inactive' ? (
                                        <Badge color="error" size="sm">{t('workflows.status.inactive')}</Badge>
                                    ) : (
                                        <Badge color="gray" size="sm">{t('workflows.status.draft')}</Badge>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{workflow.nodes.length}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {new Date(workflow.created_at).toLocaleDateString()}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/admin/workflows/${workflow.workflow_id}`}
                                            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                        >
                                            {t('workflows.actions.view')}
                                            <Eye className="size-4" />
                                        </Link>
                                        {!workflow.is_default && (
                                            <>
                                                <Button
                                                    onClick={() => navigate(`/admin/workflows/${workflow.workflow_id}/edit`)}
                                                    size="sm"
                                                    color="secondary"
                                                    iconLeading={Edit01}
                                                >
                                                    {t('workflows.actions.edit')}
                                                </Button>
                                                <Button
                                                    onClick={() => handleDeleteClick(workflow.workflow_id)}
                                                    size="sm"
                                                    color="secondary-destructive"
                                                    iconLeading={Trash01}
                                                >
                                                    {t('workflows.actions.delete')}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {workflows.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('workflows.noData.title')}</h3>
                        <p className="text-tertiary">{t('workflows.noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('workflows.modal.confirmDelete')}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('workflows.modal.confirmDeleteMessage')}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button onClick={handleDeleteCancel} size="md" color="secondary">
                                        {t('common:modal.cancel')}
                                    </Button>
                                    <Button onClick={handleDeleteConfirm} size="md" color="primary-destructive">
                                        {t('common:modal.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
